import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { Suspense, type JSX } from "react";
import settings from "../app-settings.json";
import "../assets/css/react-html5-camera.css";
import "../assets/css/scadalynx-web.css";
import { PrivateRoute } from "../components/private-route/PrivateRoute";
import { LayoutContextProvider } from "../contexts/layout-context/LayoutContextProvider";
import { LiveDataContextProvider } from "../contexts/live-data-context/LiveDataContextProvider";
import { ProgressContextProvider } from "../contexts/progress-context/ProgressContext";
import { useUserContext } from "../contexts/user-context/UserContext";
import DefaultLayout from "../layouts/default-layout/DefaultLayout";
import FullLayout from "../layouts/full-layout/FullLayout";
// import i18n (needs to be bundled ;)) 
import { createBrowserRouter, redirect, RouterProvider } from "react-router";
import ErrorBoundary from "../components/error-boundary/ErrorBoundary";
import LoadingZone from "../components/loading-zone/LoadingZone";
import { RootLayout } from "../layouts/root-layout/RootLayout";
import { routes, UIViewCategory } from "../routes/routes";
import "../services/localization-service/i18n";
import "./App.css";
import { DashboardContextProvider } from "../contexts/dashboard-context/DashboardContext";
import { ConfigurationsContextProvider } from '../contexts/configurations-context/ConfigurationsContextProvider';
import { AppHeaderContextProvider } from '../contexts/app-header-context/AppHeaderContextProvider';
import { UserFavoritesContextProvider } from '../contexts/user-favorites-context/UserFavoritesContext';

LicenseManager.setLicenseKey(settings.agGridLicense);

// Register all enterprise features
ModuleRegistry.registerModules([AllEnterpriseModule]);

export const defaultPromptIntervalMinutes = 1440;

const defaultRoutes = routes.filter(route => route.layout === "default");
const fullRoutes = routes.filter(route => route.layout === "full");

const router = createBrowserRouter(
    [{
        element: <RootLayout />,
        errorElement: <ErrorBoundary />,
        children: [
            {
                path: "/default/*",
                element: <Default />,
                children: defaultRoutes
                    // TODO: Probably want to take a second look at this. Special casing admin view may bite us in the
                    // rear at some point. This is due to parentViewID being handled differently with admin views than
                    // views with tabs.
                    .filter(route => route.parentViewID === undefined || route.category === UIViewCategory.AdminView)
                    .map(route => {
                        return {
                            path: route.path,
                            element:
                                route.component
                                    ? <Suspense fallback={<LoadingZone isLoading={true} />}>
                                        <route.component />
                                    </Suspense>
                                    : undefined,
                            children: defaultRoutes
                                .filter(childRoute => childRoute.parentViewID === route.viewID && childRoute.category !== UIViewCategory.AdminView)
                                .map(childRoute => {
                                    return {
                                        path: childRoute.path,
                                        element: route.component
                                            ? <route.component />
                                            : undefined
                                    };
                                })
                        };
                    })
            },
            {
                path: "/*",
                element: <Full />,
                children: [
                    ...fullRoutes
                        .filter(route => route.parentViewID === undefined)
                        .map(route => {
                            return {
                                path: route.path,
                                element: route.component
                                    ? <route.component />
                                    : undefined
                            };
                        }), {
                        path: "*",
                        loader: () => redirect("/login")
                    }
                ]
            }
        ]
    }], {
    future: {
        // v7_relativeSplatPath: true,
        // v7_fetcherPersist: true,
        // v7_normalizeFormMethod: true,
        // v7_partialHydration: true,
        // v7_skipActionErrorRevalidation: true
        // v8_middleware: true
    }
});

export default function App(): JSX.Element {
    return (
        <RouterProvider router={router} />
    );
}


function Default(): JSX.Element {

    const [userState] = useUserContext();

    return (
        <PrivateRoute authenticated={userState.authenticated}>
            <ProgressContextProvider>
                <UserFavoritesContextProvider>
                <LiveDataContextProvider>
                    <LayoutContextProvider>
                        <AppHeaderContextProvider>
                            <ConfigurationsContextProvider>
                                <DashboardContextProvider>
                                    <DefaultLayout />
                                </DashboardContextProvider>
                            </ConfigurationsContextProvider>
                        </AppHeaderContextProvider>                        
                    </LayoutContextProvider>
                </LiveDataContextProvider>
                </UserFavoritesContextProvider>                
            </ProgressContextProvider>
        </PrivateRoute>
    );
}

function Full(): JSX.Element {
    return (
        <FullLayout />
    );
}
